import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Select, SelectType } from '@components/atoms/Select';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { Table } from '@components/atoms/Table';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Pagination } from '@components/molecules/Pagination';
import { Card } from '@components/atoms/Card';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { SITE_URLS } from '@constants';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useNotifications } from '@hooks/useNotifications';
import { formatPrice } from '@utils/format';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { ResultsType } from '@types';
import styles from './ManageServices.module.css';
import { useGetMyAccountServices } from '@services/useGetMyAccountServices';
import { useDeleteService } from '@services/useDeleteService';
import { ServiceStateBadge } from '@components/molecules/badges/ServiceStateBadge';
import { useGetAllServicesCategories } from '@services/useGetAllServicesCategories';

const TEXT_FILTER_ID = 'text-filter';
const CATEGORY_FILTER_ID = 'category-filter';
const STORE_FILTER_ID = 'store-filter';

export const ManageServices: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { pageToRequest, resetPageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-services-component.title'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myServices}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                </div>
                <div className="pt-4">
                    <ManagementTable formRef={formRef} resetPageToRequest={resetPageToRequest} />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

const Filters: FC<FiltersProps> = ({ formRef, pageToRequest, checkPageToRequest }) => {
    const location = useLocation();
    const pageSize = useMediaQuery();
    const { request: requestMyAccountServices } = useGetMyAccountServices();
    const { myAccountServices, isLoadingMyAccountServices, setMyAccountServices } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        addService: t('manage-services-component.add-service'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyAccountServices(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTextFilter = appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name;
    const getCategoryFilter = appliedFilters
        .find((item) => item.label === CATEGORY_FILTER_ID)
        ?.values.map((value) => value.name)?.[0];
    let getStoreFilter = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    useEffect(() => {
        // check if url states a specific store, initially
        const queryParams = new URLSearchParams(location.search);
        const getStoreParam = queryParams.get('store') || '';
        if (getStoreParam) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            getStoreFilter = getStoreParam;
            setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: getStoreParam }]);
        }
    }, []);

    const fetchServices = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myAccountServices?.pagination?.total_pages && page > myAccountServices?.pagination?.total_pages) {
            return;
        }

        requestMyAccountServices({
            textSearch: getTextFilter,
            categories_uuid: getCategoryFilter,
            stores_uuid: getStoreFilter,
            page: page,
        });
    };

    // request more services from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyAccountServices && fetchServices(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyAccountServices]);

    // request first page whenever a filter changes
    const submitForm = () => fetchServices(1);

    useEffect(() => {
        !isLoadingMyAccountServices && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myAccountServices?.pagination?.current_page === 1
                ? myAccountServices?.data || []
                : [...previous, ...(myAccountServices?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountServices]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <TextFilter />
                <div className={`d-flex flex-column flex-md-row ${styles['grouped-select-filters']}`}>
                    <CategoryFilter />
                    <StoreFilter />
                </div>
                {/* add service */}
                <div>
                    <Anchor
                        href={SITE_URLS.myServicesCreate}
                        classes={['xs', 'sm'].includes(pageSize) ? 'w-100' : ''}
                        mode={AnchorMode.primary}>
                        {translations.addService}
                    </Anchor>
                </div>
            </Form>
        </MyAccountContainer.Filters>
    );
};

interface ManagementTableProps {
    formRef: RefObject<HTMLFormElement>;
    resetPageToRequest: () => void;
}

const ManagementTable: FC<ManagementTableProps> = ({ formRef, resetPageToRequest }) => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestDelete,
        loading: loadingDelete,
        error: errorDelete,
        success: successDelete,
        lastResponseTime,
    } = useDeleteService();
    const { results: services } = useFilters();
    const { isLoadingMyAccountServices } = useData();
    const { t } = useTranslation();

    const translations = {
        name: t('manage-services-component.table.name'),
        merchant: t('manage-services-component.table.merchant'),
        category: t('manage-services-component.table.category'),
        price: t('manage-services-component.table.price'),
        condition: t('manage-services-component.table.condition'),
        actions: t('manage-services-component.table.actions'),
        deletePopupTitle: t('manage-services-component.delete-popup.title'),
        deletePopupDescription: t('manage-services-component.delete-popup.description'),
        deletePopupWarning: t('manage-services-component.delete-popup.warning'),
    };

    // uuid of service being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                if (successDelete) {
                    showDefaultNotification({ type: 'success', message: successDelete });
                }
                // reset paginated list of services
                formRef?.current?.requestSubmit();
                resetPageToRequest();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-left', text: translations.name },
                        { classes: 'text-center', hideOnMobile: true, text: translations.merchant },
                        { classes: 'text-center', hideOnMobile: true, text: translations.category },
                        { classes: 'text-end', hideOnMobile: true, text: translations.price },
                        { classes: 'text-center', text: translations.condition },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={services.map((item) => {
                        return [
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{item.name}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{item.merchant}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{item.category}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: <Typography fontSize="14">{formatPrice(item.price) || '-'}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <ServiceStateBadge state={item.status || ''} />,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key="option-1"
                                                href={SITE_URLS.myServicesEdit.replace(':uuid', `${item.uuid}`)}
                                                mode={AnchorMode.none}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-pen-to-square" />
                                                </Typography>
                                            </Anchor>,
                                            <button
                                                key="option-2"
                                                disabled={loadingDelete}
                                                onClick={() => setShowDeleteModal(item.uuid || null)}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-trash-can" />
                                                </Typography>
                                            </button>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
            <Pagination loading={isLoadingMyAccountServices} type={ResultsType.services} />

            <PopupDelete
                question={translations.deletePopupTitle}
                confirmation={translations.deletePopupDescription}
                warning={translations.deletePopupWarning}
                onClickConfirm={handleOnConfirmDelete}
                show={showDeleteModal !== null}
                onHide={() => setShowDeleteModal(null)}
            />
        </div>
    );
};

const TextFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        searchPlaceholder: t('manage-services-component.search.placeholder'),
    };

    return (
        <div className="align-content-center" style={{ width: ['xs', 'sm', 'md'].includes(pageSize) ? '100%' : 'unset' }}>
            <Input
                type={InputTypes.text}
                variant={InputVariants.searchInput}
                placeholder={translations.searchPlaceholder}
                value={appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name || ''}
                onChange={(value) => setFilterValues(TEXT_FILTER_ID, [{ uuid: null, name: value }])}
            />
        </div>
    );
};

const CategoryFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { allServicesCategories, isLoadingAllServicesCategories, isLoadingMyAccountServices } = useData();
    const { request: requestAllServicesCategories } = useGetAllServicesCategories();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-services-component.category.label'),
        default: t('select-default-option'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allServicesCategories && !allServicesCategories && requestAllServicesCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allServicesCategories]);

    const options = [
        { groupName: '', groupOptions: [{ value: '', text: translations.default }] },
        ...(allServicesCategories?.data || []).map((group) => {
            return {
                groupName: group.name || '',
                groupOptions:
                    group.children?.map((item) => {
                        return { value: item.uuid || '', text: item.name || '' };
                    }) || [],
            };
        }),
    ];

    const selected = appliedFilters.find((item) => item.label === CATEGORY_FILTER_ID)?.values.map((value) => value.name)?.[0];

    const handleOnChange: (value: string) => void = (value) => setFilterValues(CATEGORY_FILTER_ID, [{ uuid: null, name: value }]);

    return (
        <div className="d-flex align-items-center">
            <Typography classes="text-nowrap mb-0 me-2" fontSize="16">
                {translations.title}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '146px'}
                type={SelectType.variant2}
                disabled={allServicesCategories === null || isLoadingAllServicesCategories || isLoadingMyAccountServices}
                options={options}
                value={selected}
                onChange={handleOnChange}
            />
        </div>
    );
};

const StoreFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { allMyAccountStores, isLoadingAllMyAccountStores, isLoadingMyAccountServices } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-services-component.store.label'),
        default: t('select-default-option'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    const options = [
        { value: '', text: translations.default },
        ...(allMyAccountStores?.data || [])
            // filter by stores that may have services
            .filter((item) => !item.is_product_store)
            .map((item) => {
                return { value: item.uuid || '', text: item.name || '' };
            }),
    ];

    const selected = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    const handleOnChange: (value: string) => void = (value) => setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: value }]);

    return (
        <div className="d-flex align-items-center">
            <Typography classes="text-nowrap mb-0 me-2" fontSize="16">
                {translations.title}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '146px'}
                type={SelectType.variant2}
                options={options}
                disabled={allMyAccountStores === null || isLoadingAllMyAccountStores || isLoadingMyAccountServices}
                value={selected}
                onChange={handleOnChange}
            />
        </div>
    );
};
